import {
	Directive,
	ElementRef,
	AfterViewInit,
	AfterContentInit,
	OnDestroy,
	signal,
	Renderer2,
	effect,
	Input,
	HostListener,
	ChangeDetectorRef
} from '@angular/core';
import { GridApi } from 'ag-grid-community';
import { debounce } from 'lodash-es';

/**
 * Directive used to make a container automatically resize to fit a child AG Grid instance.
 *
 * @example ```html
 * <prism-card shAgGridAutoSizeContainer [gridApi]="gridApi">
 *   <ag-grid-angular
 *     ...some attributes
 *   ></ag-grid-angular>
 * </prism-card>
 * ```
 */
@Directive({
	standalone: true,
	selector: '[shAgGridAutoSizeContainer]'
})
export class AgGridAutoSizeContainerDirective implements AfterViewInit, AfterContentInit, OnDestroy {
	@Input({ required: true })
	public set gridApi(value: GridApi | null | undefined) {
		if (value) {
			this.localGridApi = value;
			this.localGridApi.addGlobalListener((eventName: string) => {
				this.handleGridApiEvent(eventName);
			});

			this.debouncedUpdateGridHeight();
		}
	}

	/**
	 * Min height that is only used when the no rows overlay is showing.
	 */
	@Input({ required: false }) public minNoRowsHeight?: number;

	// /**
	//  * Min height that is only used when the loading overlay is showing.
	//  */
	@Input({ required: false }) public minLoadingHeight?: number;

	// /**
	//  * Min height used when there are no rows available, such as when the no rows or loading overlays are shown.
	//  * NOTE: the other 2 more specific min height options take precedence over this one
	//  */
	@Input({ required: false }) public minHeight?: number;

	public localGridApi!: GridApi;
	public gridElement!: HTMLElement;
	public overlayObserver?: MutationObserver;

	public gridHeight = signal(-1);
	public containerHeight = signal(-1);

	constructor(
		private elementRef: ElementRef,
		private readonly renderer: Renderer2,
		private readonly cdr: ChangeDetectorRef
	) {
		effect(() => {
			const tempContainerHeight = this.containerHeight();
			const tempGridHeight = this.gridHeight();

			// make sure the grid is initialized and we've received updated
			// values for the container and grid before applying a height.
			if (!this.localGridApi || !this.gridElement || tempContainerHeight < 0 || tempGridHeight < 0) {
				return;
			}

			// In cases where the grid's container does not fill its available vertical space on init, we need to use
			// the grid's height on first render to ensure the container grows. One example of this is rendering a grid
			// inside of a Prism card where the card needs to expand along with the grid.
			const newHeight = tempContainerHeight <= 4 ? tempGridHeight : Math.min(tempContainerHeight, tempGridHeight);
			this.renderer.setStyle(this.gridElement, 'height', `${newHeight}px`);
		});
	}

	@HostListener('resize')
	public onResize(event: ResizeObserverEntry): void {
		this.containerHeight.set(event.target.clientHeight);
	}

	public ngAfterViewInit(): void {
		this.registerOverlayObserver();
		this.containerHeight.set(this.elementRef.nativeElement.offsetHeight);
	}

	public ngAfterContentInit(): void {
		this.gridElement = this.elementRef.nativeElement.querySelector('ag-grid-angular');
		if (!this.gridElement) {
			throw new Error('unable to find ag-grid element as child of component');
		}

		this.gridHeight.set(this.gridElement.offsetHeight);
	}

	public ngOnDestroy(): void {
		if (this.overlayObserver) {
			this.overlayObserver.disconnect();
		}
	}

	private handleGridApiEvent(event: string): void {
		switch (event) {
			case 'columnRowGroupChanged':
			case 'displayedColumnsChanged':
			case 'displayedRowsChanged':
			case 'gridSizeChanged':
			case 'bodyHeightChanged':
			case 'gridReady':
				this.debouncedUpdateGridHeight();
				break;
			default:
				break;
		}
	}

	/**
	 * By debouncing the update, we ensure that the grid height is only updated after a short delay
	 * To prevent flickers caused by the repeated execution of forEachNodeAfterFilter, debounce is necessary.
	 */
	private debouncedUpdateGridHeight = debounce(() => {
		this.gridHeight.set(this.computeMaxGridTableHeight());

		// It seems that change detection needs to be triggered manually for the effect to run when this
		// directive is placed on a Prism component.
		this.cdr.detectChanges();
	}, 100);

	private computeMaxGridTableHeight(): number {
		if (!this.localGridApi) return 0;

		const groupDropZoneHeight = this.queryElementHeight('.ag-column-drop');
		const headerHeight = this.queryElementHeight('.ag-header');
		const scrollBarHeight = this.queryElementHeight('.ag-body-horizontal-scroll');
		const statusBarHeight = this.queryElementHeight('.ag-status-bar');

		// w/out the +5, the vertical scrollbar would sometimes appear unnecessarily.
		// testing has shown that 5 is the smallest value required to prevent this.
		const gridShellHeight = groupDropZoneHeight + headerHeight + scrollBarHeight + statusBarHeight + 5;

		let gridBodyHeight = 0;
		this.localGridApi.forEachNodeAfterFilter((node) => {
			// if a row is under a non-expanded group, don't take its height into account
			if (node.displayed) {
				gridBodyHeight += node.rowHeight ?? 0;
			}
		});

		// If there are no rows, compute the min height based on which overlay is showing and what inputs were provided
		// - If minNoRowsHeight/minLoadingHeight was provided, use it
		// - Otherwise, try to use the height of the overlay child component
		// - As a final fallback, use the standard minHeight or the default row height
		if (gridBodyHeight === 0) {
			const noRowsOverlayWrapper: HTMLElement =
				this.elementRef.nativeElement.querySelector('.ag-overlay-no-rows-wrapper');
			const loadingOverlayWrapper: HTMLElement =
				this.elementRef.nativeElement.querySelector('.ag-overlay-loading-wrapper');

			if (noRowsOverlayWrapper) {
				gridBodyHeight += this.minNoRowsHeight ?? this.getOverlayHeight(noRowsOverlayWrapper);
			} else if (loadingOverlayWrapper) {
				gridBodyHeight += this.minLoadingHeight ?? this.getOverlayHeight(loadingOverlayWrapper);
			}
		}

		return gridShellHeight + gridBodyHeight;
	}

	private queryElementHeight(selector: string): number {
		const el = this.elementRef.nativeElement.querySelector(selector);
		return el ? el.clientHeight : 0;
	}

	private getOverlayHeight(overlayWrapper: HTMLElement | null): number {
		if (overlayWrapper?.firstChild) {
			return (<HTMLElement>overlayWrapper.firstChild).clientHeight;
		}

		return this.minHeight ?? this.localGridApi.getGridOption('rowHeight') ?? 50;
	}

	private registerOverlayObserver(): void {
		const overlay: HTMLElement | null = this.elementRef.nativeElement.querySelector('.ag-overlay');
		if (!overlay) {
			return;
		}

		// Observe the overlay component and recompute the grid height whenever the classes change.
		// AG Grid always keeps an element with the .ag-overlay class in the DOM, but adds/removes the
		// .ag-hidden class depending on visibility.
		this.overlayObserver = new MutationObserver(() => {
			this.gridHeight.set(this.computeMaxGridTableHeight());
		});

		this.overlayObserver.observe(overlay, { attributeFilter: ['class'] });
	}
}
